import { useRechargeCooldown } from "./rechargeCooldown";
import { getRechargeParamsKey } from "./rechargeParamsRefresh";
import { useEffect, useRef, useState } from "react";
import { fetcher, useAPI, useAPIMutation } from "@/api/api";
import { processMobilePrefix } from "@/utils/mobileUtils";
import {
  IFInitWalletRechargeCryptoReq,
  IFInitWalletRechargeFlatReq,
  IFInitWalletSwitchReq,
  IFInitWalletWithdrawCryptoReq,
  IFInitWalletWithdrawFlatReq,
  IFWalletExchangeReq,
  IFWalletRateReq,
  IFWalletRechargeCryptoReq,
  IFWalletRechargeFlatReq,
  IFWalletWithdrawCryptoReq,
  IFWalletWithdrawFlatReq,
} from "@/api/apiReq/IFWalletReq";
import {
  IFInitWalletRechargeCryptoRsp,
  IFInitWalletRechargeFlatRsp,
  IFInitWalletRsp,
  IFInitWalletWithdrawCryptoRsp,
  IFInitWalletWithdrawFlatRsp,
  IFWalletExchangeRsp,
  IFWalletRateRsp,
  IFWalletRechargeCryptoRsp,
  IFWalletRechargeFlatRsp,
  IFWalletRsp,
  IFWalletWithdrawCryptoRsp,
  IFWalletWithdrawFlatRsp,
  IFWithdrawFlatConfigRsp,
} from "@/api/apiRsp/IFWalletRsp";
import {
  AmountDivisorConversion,
  dealAmount0Fiexd,
} from "@/Core/utils/currencyDealCenter";
import useSWR from "swr";
import { sys } from "@/Core/utils/system";
import { isArray } from "@/utils/is";
import { getCurrentCurrency } from "@/utils/countryConfigUtils";
import { trackEmEvent } from "@/features/analytics/em/sdk";

const getErrorCode = (error: any): string => {
  return String(error?.code || error?.errorCode || "unknown");
};

const getPayUrlHost = (payUrl?: string): string => {
  if (!payUrl) {
    return "";
  }

  try {
    return new URL(payUrl).host;
  } catch (_error) {
    return "";
  }
};

export class SWRWalletKey {
  //切换法币充值
  public static get changeFlatRecharge() {
    return `changeFlatRecharge?${sys.token}`;
  }

  //获取兑换汇率
  public static get exchangeRateConfig() {
    return `exchangeRateConfig?${sys.token}`;
  }

  //获取所有钱包数据
  public static get walletAll() {
    return sys.token ? `walletAll${sys.token}` : null;
  }

  //切换法币提现
  public static get changeFlatWidthdraw() {
    return `changeFlatWidthdraw?${sys.token}`;
  }

  public static get widthdrawFlatConfig() {
    return `widthdrawFlatConfig${sys.token}`;
  }
}

/**
 * 钱包切换
 */
export function useWalletSwitch() {
  const { isMutating, trigger, data, reset, error } = useAPIMutation<
    IFInitWalletSwitchReq,
    IFInitWalletSwitchReq
  >({
    path: "/api/user/wallet/switch",
    type: "post",
    formData: false,
  });

  return {
    trigger,
    data,
    reset,
    isMutating,
    isLoading: isMutating,
    error,
    errorCode: error?.code,
  };
}

const findRecently = (gears, rates) => {
  // 处理 null 或 undefined 的情况
  if (
    !gears ||
    !rates ||
    !Array.isArray(gears) ||
    !Array.isArray(rates) ||
    gears.length === 0 ||
    rates.length === 0
  ) {
    return [];
  }

  const arr = [];
  const ret = [];

  for (let i = 0; i < rates.length; ++i) {
    //寻找存在返利规则情况下的充值金额<定额面值数据,自定义封装新数组(定额面值,符合条件的返利比例/充值金额)
    for (let j = 0; j < gears.length; ++j) {
      if (Number(rates[i].rechargeAmount) <= Number(gears[j])) {
        arr.push({
          gear: gears[j],
          ...rates[i],
        });
      } else {
        arr.push({
          gear: gears[j],
        });
      }
    }
  }
  for (let i = 0; i < gears.length; ++i) {
    //遍历定额面值与自定义数组,过滤
    let v = arr[i];
    for (let j = 1; j < arr.length; ++j) {
      if (
        v.gear === arr[j].gear &&
        Number(v.rechargeAmount) < Number(arr[j].rechargeAmount)
      ) {
        v = arr[j];
      }
    }
    ret.push(v);
  }
  return ret;
};

/**
 * 加密货币币种切换，数据
 */
export function useInitCryptoSwitch() {
  const { isMutating, trigger, data, reset, error } = useAPIMutation<
    IFInitWalletRechargeCryptoRsp,
    IFInitWalletRechargeCryptoReq
  >({
    path: "/payment/crypto/params",
    type: "post",
  });

  return {
    trigger,
    data,
    reset,
    isMutating,
    isLoading: isMutating,
    error,
    errorCode: error?.code,
  };
}
/**
 * 法币币种切换，初始化数据
 */
export function useInitFlatSwitch() {
  const { isMutating, trigger, data, reset, error } = useAPIMutation<
    IFInitWalletRechargeFlatRsp,
    IFInitWalletRechargeFlatReq
  >({
    path: "/payment/crypto/params",
    type: "post",
  });

  return {
    trigger,
    data,
    reset,
    isMutating,
    isLoading: isMutating,
    error,
    errorCode: error?.code,
  };
}

/**
 * 钱包--提现加密初始化
 */
export function useInitWithdrawCrypto(params) {
  const { data, isLoading, isValidating, error, revalidate } = useAPI<
    IFInitWalletWithdrawCryptoRsp[],
    IFInitWalletWithdrawCryptoReq
  >(
    {
      path: "/payment/withdraw/crypto/params/list",
      type: "post",
      formData: false,
      needLogin: true,
    },
    params
  );
  // useMessageError(error)
  return {
    data,
    isValidating: isValidating,
    error,
    errorCode: error?.code,
    isLoading,
    revalidate,
  };
}
/**
 * 钱包--提现加密初始化
 */
export function useInitWithdrawCryptoMu() {
  const { isMutating, trigger, data, reset, error } = useAPIMutation<
    IFInitWalletWithdrawCryptoRsp[],
    IFInitWalletWithdrawCryptoReq
  >({
    path: "/payment/withdraw/crypto/params/list",
    type: "post",
    formData: false,
    needLogin: true,
  });
  return {
    trigger,
    data,
    reset,
    isMutating,
    error,
    errorCode: error?.code,
  };
}

export function useWithdrawFlat(params) {
  const fetch = () => {
    return fetcher<IFWithdrawFlatConfigRsp>(
      {
        path: "/payment/withdraw/flat/params/list",
        type: "post",
      },
      { arg: params }
    ).then((res: IFWithdrawFlatConfigRsp) => {
      return res;
    });
  };
  return useSWR<IFWithdrawFlatConfigRsp, IFInitWalletWithdrawFlatReq>(
    SWRWalletKey.widthdrawFlatConfig + JSON.stringify(params),
    fetch,
    {
      dedupingInterval: 1 * 1000,
    }
  );
}

/**
 * 钱包--提现法币初始化useAPIMutation
 */
export function useChangeWalletWithdrawFlat(
  params?: IFInitWalletWithdrawFlatReq
) {
  const fetch = () => {
    return fetcher<IFInitWalletWithdrawFlatRsp[]>(
      {
        path: "/payment/withdraw/flat/params/list",
        type: "post",
        formData: false,
      },
      { arg: params }
    );
  };
  return useSWR<IFInitWalletWithdrawFlatRsp[]>(
    SWRWalletKey.changeFlatWidthdraw,
    fetch,
    {
      dedupingInterval: 60 * 1000,
    }
  );
}
export function useInitWithdrawFlatMu() {
  const { isMutating, trigger, data, reset, error } = useAPIMutation<
    IFInitWalletWithdrawFlatRsp[],
    IFInitWalletWithdrawFlatReq
  >({
    path: "/payment/withdraw/flat/params/list",
    type: "post",
    formData: false,
    needLogin: true,
  });
  return {
    trigger,
    data,
    reset,
    isMutating,
    error,
    errorCode: error?.code,
  };
}

/**
 * 钱包--充值加密提交
 */
export function useWalletRechargeCrypto() {
  const { isRechargeBlocked, runRecharge } = useRechargeCooldown();
  const { isMutating, trigger: originalTrigger, data, reset, error } = useAPIMutation<
    IFWalletRechargeCryptoRsp,
    IFWalletRechargeCryptoReq
  >({
    path: "/payment/pay/crypto",
    type: "post",
  });

  const trigger = (params: IFWalletRechargeCryptoReq) => {
    const trackProps = {
      walletType: "crypto",
      currencyType: params.currencyType || "",
      money: params.money,
      payCode: params.payCode || "",
      hasMobile: false,
    };

    return runRecharge(() => {
      trackEmEvent("wallet_recharge_submit", trackProps);

      return originalTrigger(params)
        .then((res) => {
          trackEmEvent("wallet_recharge_redirect_ready", {
            ...trackProps,
            payUrlHost: getPayUrlHost(res?.payUrl),
          });
          return res;
        })
        .catch((triggerError) => {
          trackEmEvent("wallet_recharge_fail", {
            ...trackProps,
            errorCode: getErrorCode(triggerError),
          });
          throw triggerError;
        });
    });
  };
  return {
    trigger,
    data,
    reset,
    isMutating,
    isRechargeBlocked,
    errorCode: error?.code,
  };
}
/**
 * 钱包--充值法币提交
 */
export function useWalletRechargeFlat() {
  const { isRechargeBlocked, runRecharge } = useRechargeCooldown();
  const {
    isMutating,
    trigger: originalTrigger,
    data,
    reset,
    error,
  } = useAPIMutation<IFWalletRechargeFlatRsp, IFWalletRechargeFlatReq>({
    path: "/payment/pay/flat",
    type: "post",
  });

  const trigger = (params: IFWalletRechargeFlatReq) => {
    const { rechargeMobileUserId, ...requestParams } = params;
    // 使用统一的手机号处理方法
    const processedParams = {
      ...requestParams,
      mobile: params.mobile ? processMobilePrefix(params.mobile) : params.mobile,
    };
    const trackProps = {
      walletType: "flat",
      currencyType: processedParams.currencyType || "",
      money: processedParams.money,
      payCode: processedParams.payCode || "",
      hasMobile: !!processedParams.mobile,
    };

    return runRecharge(() => {
      trackEmEvent("wallet_recharge_submit", trackProps);

      return originalTrigger(processedParams)
        .then((res) => {
          trackEmEvent("wallet_recharge_redirect_ready", {
            ...trackProps,
            payUrlHost: getPayUrlHost(res?.payUrl),
          });
          // 充值成功后，保存手机号到localStorage
          if (res && processedParams.mobile) {
            import("@/utils/mobileUtils").then(({ saveMobileToStorage }) => {
              saveMobileToStorage(processedParams.mobile, rechargeMobileUserId);
            });
          }
          return res;
        })
        .catch((triggerError) => {
          trackEmEvent("wallet_recharge_fail", {
            ...trackProps,
            errorCode: getErrorCode(triggerError),
          });
          throw triggerError;
        });
    });
  };

  return {
    trigger,
    data,
    reset,
    isMutating,
    isRechargeBlocked,
    errorCode: error?.code,
  };
}

/**
 * 钱包--提现加密提交
 */
export function useWalletWithdrawCrypto() {
  const { isMutating, trigger: originalTrigger, data, reset, error } = useAPIMutation<
    IFWalletWithdrawCryptoRsp,
    IFWalletWithdrawCryptoReq
  >({
    path: "/payment/withdraw/crypto",
    type: "post",
  });

  const trigger = (params: IFWalletWithdrawCryptoReq) => {
    const trackProps = {
      walletType: "crypto",
      currencyType: params.currencyType || "",
      money: params.money,
      networkType: params.networkType || "",
      hasAccountInfo: !!params.address,
    };

    trackEmEvent("wallet_withdraw_submit", trackProps);

    return originalTrigger(params)
      .then((res) => {
        trackEmEvent("wallet_withdraw_success", trackProps);
        return res;
      })
      .catch((triggerError) => {
        trackEmEvent("wallet_withdraw_fail", {
          ...trackProps,
          errorCode: getErrorCode(triggerError),
        });
        throw triggerError;
      });
  };
  return {
    trigger,
    data,
    reset,
    isMutating,
    errorCode: error?.code,
  };
}
/**
 * 钱包--提现法币提交
 */
export function useWalletWithdrawFlat() {
  const {
    isMutating,
    trigger: originalTrigger,
    data,
    reset,
    error,
  } = useAPIMutation<IFWalletWithdrawFlatRsp, IFWalletWithdrawFlatReq>({
    path: "/payment/withdraw/flat",
    type: "post",
  });

  const trigger = (params: IFWalletWithdrawFlatReq) => {
    const { withdrawMobileUserId, ...requestParams } = params;
    // 使用统一的手机号处理方法
    const processedParams = {
      ...requestParams,
      mobile: params.mobile ? processMobilePrefix(params.mobile) : params.mobile,
    };
    const trackProps = {
      walletType: "flat",
      currencyType: processedParams.currencyType || "",
      money: processedParams.money,
      merchantId: processedParams.merchantId || "",
      hasMobile: !!processedParams.mobile,
      hasAccountInfo: !!processedParams.accountInfo,
    };

    trackEmEvent("wallet_withdraw_submit", trackProps);

    return originalTrigger(processedParams)
      .then((res) => {
        trackEmEvent("wallet_withdraw_success", trackProps);
        // 提现成功后，保存手机号到localStorage
        if (res && processedParams.mobile) {
          import("@/utils/mobileUtils").then(({ saveMobileToStorage }) => {
            saveMobileToStorage(processedParams.mobile, withdrawMobileUserId);
          });
        }
        return res;
      })
      .catch((triggerError) => {
        trackEmEvent("wallet_withdraw_fail", {
          ...trackProps,
          errorCode: getErrorCode(triggerError),
        });
        throw triggerError;
      });
  };

  return {
    trigger,
    data,
    reset,
    isMutating,
    errorCode: error?.code,
  };
}

/**
 * 钱包--兑换货币
 */
export function useWalletExchange() {
  const { isMutating, trigger, data, reset, error } = useAPIMutation<
    IFWalletExchangeRsp,
    IFWalletExchangeReq
  >({
    path: "/api/user/wallet/exchange",
    type: "post",
  });
  return {
    trigger,
    data,
    reset,
    isMutating,
    error,
    errorExcharge: error,
  };
}

/**
 * 兑换钱包--查询基准货币的汇率兑换信息
 */
export function useGetWalletRateConfig(params?: IFWalletRateReq) {
  const fetch = () => {
    return fetcher<IFWalletRateRsp>(
      { path: "/api/currency/rate/config", type: "get" },
      { arg: params }
    );
  };
  return useSWR<IFWalletRateRsp>(
    params
      ? `${SWRWalletKey.exchangeRateConfig}${JSON.stringify(params)}`
      : null,
    fetch,
    { dedupingInterval: 60 * 1000 }
  );
}
export function useWalletRateSwitch() {
  const { isMutating, trigger, data, reset, error } = useAPIMutation<
    IFWalletRateRsp,
    IFWalletRateReq
  >(
    { path: "/api/currency/rate/config", type: "get" },
    { dedupingInterval: 0 }
  );
  return {
    trigger,
    data,
    rate: data?.rate || "",
    reset,
    isMutating,
    error,
    errorRate: error,
  };
}

/**
 * 钱包初始化
 */
export function useWalletAll(revalidateOnMount = true) {
  const fetch = () => {
    return fetcher<IFWalletRsp>(
      { path: "/api/user/wallet/all", type: "get" },
      {}
    ).then((data: any) => {
      // 获取当前国家配置的货币（服务器可能返回BRL，但我们要使用配置的货币）
      const correctCurrency = getCurrentCurrency();
      
      data.all = [];
      data.swap = [];
      data.flat = [];
      data.crypto = [];
      data.platform = [];
      if (isArray(data)) {
        data?.forEach?.((v) => {
          const item = { ...v };
          
          // 保存服务端原始货币类型用于精度计算
          item.serverCurrencyType = v.currencyType;
          
          // 覆盖服务器返回的货币，使用配置中的货币（用于显示）
          // 只覆盖法币钱包的货币类型，加密货币保持不变
          if (item.currencyCategory === 2) {
            item.currencyType = correctCurrency;
            // 如果有currency字段也需要覆盖
            if (item.currency) {
              item.currency = correctCurrency;
            }
          }
          
          item.balance = v.balance;
          item.gameBalance = v.gameBalance;
          item.allBalance = item.balance + item.gameBalance;
          if (item.currencyCategory === 1) {
            //加密
            data.swap.push(item);
            // 不是平台币 就放到加密货币钱包
            !item?.isPlatform && data.crypto.push(item);
          } else if (item.currencyCategory === 2) {
            data.swap.push(item);
            //法币
            data.flat.push(item);
          }
          data.all.push(item);
          if (item.isPlatform) {
            data.platform.push(item);
          }
        });
      }
      return data;
    });
  };
  return useSWR<IFWalletRsp>(SWRWalletKey.walletAll, fetch, {
    dedupingInterval: 10 * 1000,
    revalidateOnMount: revalidateOnMount,
  });
}

/**
 * 加密货币充值选择
 */
export function useRechargeCryptoSelect(params?: IFInitWalletRechargeCryptoReq) {
  const result = useSWR<IFInitWalletRechargeCryptoRsp>(
    getRechargeParamsKey("crypto", sys.token, params),
    async () => {
      const data = await fetcher<IFInitWalletRechargeCryptoRsp>(
        { path: "/payment/crypto/params", type: "post" }, { arg: params },
      );
      const rules = data?.rebateRuleItems?.sort((a, b) =>
        AmountDivisorConversion(a?.rechargeAmount, { currencyType: params.currencyType }) -
        AmountDivisorConversion(b?.rechargeAmount, { currencyType: params.currencyType })
      );
      return {
        ...data,
        custGears: rules?.length ? findRecently(data?.gears, rules) : [],
      };
    },
    { dedupingInterval: 1000, revalidateOnMount: true, revalidateOnFocus: true },
  );
  return { ...result, cryptoCfg: result.data };
}

// 切换法币充值方式
export function useChangeFlatRecharge(params?: IFInitWalletRechargeFlatReq) {
  const fetch = () => {
    return fetcher<IFInitWalletRechargeFlatRsp>(
      { path: "/payment/flat/params/list", type: "post" },
      {
        arg: params,
      }
    ).then((data) => {
      //accountRebateRule 排序
      const accountRebateRule = data?.accountRebateRule?.sort((a, b) => {
        //排序
        return (
          AmountDivisorConversion(a?.minRechargeAmount, {
            currencyType: a?.rechargeCurrencyType,
          }) -
          AmountDivisorConversion(b?.minRechargeAmount, {
            currencyType: b?.rechargeCurrencyType,
          })
        );
      });
      if (data && data.accountRebateRule) {
        data.accountRebateRule = accountRebateRule;
      }
      const arrRuleItems = data?.rebateRuleItems?.sort((a, b) => {
        //排序
        return (
          AmountDivisorConversion(a?.rechargeAmount, {
            currencyType: params?.currencyType,
          }) -
          AmountDivisorConversion(b?.rechargeAmount, {
            currencyType: params?.currencyType,
          })
        );
      });
      // 对gears排序 - 添加安全检查
      data?.payParams?.forEach?.((v) => {
        if (v?.gears && Array.isArray(v.gears)) {
          v.gears.sort((a, b) => {
            return Number(a) - Number(b);
          });
        }
      });

      //如果有新活动 使用新活动的派彩规则
      if (accountRebateRule && accountRebateRule.length) {
        accountRebateRule.forEach((v: any) => {
          v.rechargeAmount = v.minRechargeAmount;
        });
        // data?.payParams?.forEach((itemPay)=>{
        //   //
        //   const custArr = arrRuleItems?.length > 0 && findRecently(itemPay?.gears, accountRebateRule);
        // })
        data?.payParams?.forEach((itemPay) => {
          const custArr = findRecently(itemPay?.gears, accountRebateRule);
          if (!custArr || !custArr.length) {
            const arr = [];
            // 安全检查 gears 是否存在且为数组
            if (itemPay?.gears && Array.isArray(itemPay.gears)) {
              for (let i = 0; i < itemPay.gears.length; ++i) {
                arr.push({
                  gear: itemPay.gears[i],
                  rate: 0,
                  rebateRate: 0,
                });
              }
            }
            itemPay.custGears = arr;
          } else {
            itemPay.custGears = custArr;
          }

          itemPay.rebateRuleItems = arrRuleItems;
          //itemPay.rebateRuleItemsNew = accountRebateRule;
          itemPay.limitRebateAmount = data.limitRebateAmount;
          itemPay.limitRebateAmountCurrencyType =
            data.limitRebateAmountCurrencyType;
          itemPay.accountRebateRule = data?.accountRebateRule;
          itemPay.currencyRateInfo = data?.currencyRateInfo;
        });
      } else {
        // 匹配合适的派彩规则
        data?.payParams?.forEach((itemPay) => {
          const custArr =
            arrRuleItems?.length > 0 &&
            findRecently(itemPay?.gears, arrRuleItems);
          if (!custArr || !custArr.length) {
            const arr = [];
            // 安全检查 gears 是否存在且为数组
            if (itemPay?.gears && Array.isArray(itemPay.gears)) {
              for (let i = 0; i < itemPay.gears.length; ++i) {
                arr.push({
                  gear: itemPay.gears[i],
                  rate: 0,
                  rebateRate: 0,
                });
              }
            }
            itemPay.custGears = arr;
          } else {
            itemPay.custGears = custArr;
          }

          itemPay.rebateRuleItems = arrRuleItems;
          itemPay.limitRebateAmount = data.limitRebateAmount;
          itemPay.limitRebateAmountCurrencyType =
            data.limitRebateAmountCurrencyType;
          itemPay.accountRebateRule = data?.accountRebateRule;
          itemPay.currencyRateInfo = data?.currencyRateInfo;
        });
      }
      return data;
    });
  };
  return useSWR<IFInitWalletRechargeFlatRsp>(
    getRechargeParamsKey("flat", sys.token, params),
    fetch,
    {
      dedupingInterval: 1 * 1000,
      revalidateOnMount: true,
      revalidateOnFocus: true,
    }
  );
}
